import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Container, Row, Col, Card, Dropdown } from "react-bootstrap";

import SmallStats from "../components/common/SmallStats";
import "../assets/styling/Styles.css";
import "./Styles.css";
import "./Overview.css";
import LeafletMap from "./map";
import SignalCellularNodataIcon from "@mui/icons-material/SignalCellularNodata";
import SignalCellular4BarIcon from "@mui/icons-material/SignalCellular4Bar";
import { Sun, CloudSun, CloudMoon, Cloud, CloudDrizzle, CloudRain, CloudLightning, CloudSnow, Haze, Moon, MapPinned, Radio, Plus, Tractor, ArrowUpRight } from "lucide-react";

import api from "../api/api";
import { useTranslation } from "react-i18next";
import FilterFields from "./FilterFields";
import swal from "sweetalert";
import useSensorData from "../utils/useSensorData";

import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw/dist/leaflet.draw";

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) {
    return { key: "greeting_morning", Icon: Sun };
  }
  if (hour < 18) {
    return { key: "greeting_afternoon", Icon: CloudSun };
  }
  return { key: "greeting_evening", Icon: Moon };
};

// Theme-aware SVGs keep every weather condition readable on light and dark cards.
const getWeatherIcon = (code = "") => {
  const night = code.endsWith("n");
  switch (code.slice(0, 2)) {
    case "01": return night ? Moon : Sun;
    case "02": return night ? CloudMoon : CloudSun;
    case "03":
    case "04": return Cloud;
    case "09": return CloudDrizzle;
    case "10": return CloudRain;
    case "11": return CloudLightning;
    case "13": return CloudSnow;
    case "50": return Haze;
    default: return Cloud;
  }
};

const Overview = (props) => {
  const [farms, setFarms] = useState([]);

  const navigate = useNavigate();

  const [configMap] = useState({
    draw: {
      polygon: false,
      circle: false,
      rectangle: false,
      polyline: false,
      marker: false,
      circlemarker: false,
    },
    edit: {
      delete: false,
      edit: false,
    },
  });

  const { t } = useTranslation();

  const [fieldStats, setFS] = useState([]);
  const [sensorStats, setSensorStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [sensorState, setSensorState] = useState({
    online: 0,
    offline: 0,
    lowBatt: 0,
    unavailable: 0,
    sensorStates: {},
  });
  const [layerFarm, setLayerFarm] = useState([]);
  const [mapConfig] = useState({
    zoom: "",
    center: [],
    fromAction: false,
  });

  let sensorsData = useSensorData("/sensor/sensor-update-data");

  // 🔥 Nouvelle fonction pour récupérer les statuts calculés dynamiquement
  const fetchFieldsWithCalculatedStatus = async () => {
    try {
      const response = await api.get("/dashboard/fields");
      const farms = response.data.farms;
      
      console.log("📌 FARMS FROM API:", farms);
      
      // Pour chaque field, récupérer son calcul et statut
      const farmsWithStatus = await Promise.all(
        farms.map(async (farm) => {
          const fieldsWithStatus = await Promise.all(
            (farm.fields || [])
              .filter(field => !field.deleted_at) // 👈 Filtrer les champs supprimés
              .map(async (field) => {
                try {
                  console.log(`🔄 Fetching calcul for field: ${field.uid}`);
                  
                  // Récupérer le calcul pour obtenir le statut calculé
                  const calculResponse = await api.get(
                    `/calcul/field-calcul/${field.uid}?allowEmpty=true`
                  );
                  
                  const computedStatus = calculResponse.data.field?.status || field.status || "Optimal";
                  
                  console.log(`✅ Field ${field.name} (${field.uid}) - Status: ${computedStatus}`);
                  
                  return {
                    ...field,
                    status: computedStatus,
                    calculData: calculResponse.data
                  };
                } catch (err) {
                  console.error(`❌ Error fetching calcul for field ${field.uid}:`, err);
                  return {
                    ...field,
                    status: field.status || "Optimal"
                  };
                }
              })
          );
          
          return {
            ...farm,
            fields: fieldsWithStatus
          };
        })
      );
      
      setFS(farmsWithStatus);
      
      console.log("📌 FIELDS WITH CALCULATED STATUS:", farmsWithStatus);
    } catch (error) {
      console.error("❌ Error fetching fields with status:", error);
    }
  };

  const getSensorsStats = async () => {
    try {
      const response = await api.get("/dashboard/sensors");
      setSensorStats(Array.isArray(response.data.sensors) ? response.data.sensors : []);
      const summary = response.data.sensorSummary || {};
      setSensorState({
        online: Number(summary.online) || 0,
        offline: Number(summary.offline) || 0,
        lowBatt: Number(summary.lowBattery) || 0,
        unavailable: Number(summary.unavailable) || 0,
        sensorStates: summary.sensorStates || {},
      });
    } catch (error) {
      console.error("Unable to load sensor connectivity statistics:", error);
    }
  };

  const [crops, setCrops] = useState([]);
  const [todayWeather, setTodayWeather] = useState(null);
  const weatherFetchedRef = useRef(false);

  const getLayerFarm = async () => {
    await api.get("/farm/farms").then((res) => {
      const DataFarm = res.data.farms;
      setLayerFarm(DataFarm);
    });
  };

  // Today's weather in the hero: only fetched once
  // a farm with known coordinates exists, same lat/lon resolution Weather.js
  // uses for its own default location. Silently stays hidden (never shows a
  // broken "--" placeholder) if there's no farm yet or the call fails.
  useEffect(() => {
    if (weatherFetchedRef.current || layerFarm.length === 0) return;
    const firstFarm = layerFarm[0];
    const lat = firstFarm?.city?.lat ?? firstFarm?.Latitude ?? firstFarm?.latitude ?? firstFarm?.lat;
    const lon = firstFarm?.city?.lon ?? firstFarm?.Longitude ?? firstFarm?.longitude ?? firstFarm?.lon;
    if (lat === undefined || lon === undefined) return;

    weatherFetchedRef.current = true;
    api
      .post("/weather/get-data", { type: "day", lat, lon })
      .then((res) => {
        const current = res.data?.data;
        const main = current?.main;
        const icon = current?.weather?.[0]?.icon;
        if (main?.temp === undefined || !icon) return;
        setTodayWeather({ temp: Math.round(main.temp), icon });
      })
      .catch((error) => {
        console.log(error);
      });
  }, [layerFarm]);

  useEffect(() => {
    getLayerFarm();
    // Distinguer le premier chargement (skeleton) des rafraîchissements
    // silencieux déclenchés ensuite par l'intervalle ci-dessous.
    Promise.allSettled([
      fetchFieldsWithCalculatedStatus(),
      getSensorsStats(),
    ]).finally(() => setStatsLoading(false));
    const sensorStatusInterval = setInterval(getSensorsStats, 120000);
    return () => clearInterval(sensorStatusInterval);
  }, []);

  useEffect(() => {
    const handleRealtimeFieldStatus = (event) => {
      const nextStatus = event.detail;
      if (!nextStatus?.fieldUid) return;

      setFS((currentFarms) =>
        currentFarms.map((farm) => ({
          ...farm,
          fields: (farm.fields || []).map((field) =>
            field.uid === nextStatus.fieldUid
              ? {
                  ...field,
                  status: nextStatus.status,
                  currentSensorStatus: nextStatus,
                }
              : field,
          ),
        })),
      );
    };

    window.addEventListener(
      "smartfarm:field-status-updated",
      handleRealtimeFieldStatus,
    );
    return () => {
      window.removeEventListener(
        "smartfarm:field-status-updated",
        handleRealtimeFieldStatus,
      );
    };
  }, []);

  // 🔥 Calculer les statistiques à partir des statuts calculés
  let critical = 0;
  let full = 0;
  let opt = 0;

  fieldStats.forEach((farm) => {
    (farm.fields || []).forEach((field) => {
      const status = field.status;
      if (status) {
        if (status === "Optimal") {
          opt++;
        } else if (status === "Full") {
          full++;
        } else if (status === "Critical") {
          critical++;
        }
      }
    });
  });

  console.log("📊 STATS COMPUTED - Critical:", critical, "Full:", full, "Optimal:", opt);

  let smallStats = [
    {
      state: "Critical",
      translatedState: t("Critical"),
      icon: <i className="fas fa-tint-slash"></i>,
      label: `${t("fields")}`,
      value: critical,
      tone: "critical",
      chartLabels: [null, null, null, null, null, null, null],
      attrs: { md: "6", sm: "6" },
      datasets: [
        {
          label: "Today",
          fill: "start",
          borderWidth: 1.5,
          backgroundColor: "rgba(0, 184, 216, 0.1)",
          borderColor: "rgb(0, 184, 216)",
          data: [],
        },
      ],
    },
    {
      state: "Optimal",
      translatedState: t("Optimal"),
      icon: <i className={"far fa-check-circle"}></i>,
      label: `${t("fields")}`,
      value: opt,
      tone: "optimal",
      chartLabels: [null, null, null, null, null, null, null],
      attrs: { md: "6", sm: "6" },
      datasets: [
        {
          label: "Today",
          fill: "start",
          borderWidth: 1.5,
          backgroundColor: "rgba(23,198,113,0.1)",
          borderColor: "rgb(23,198,113)",
          data: [],
        },
      ],
    },
    {
      state: "Full",
      translatedState: t("Full"),
      icon: <i className="fas fa-tint"></i>,
      label: `${t("fields")}`,
      value: full,
      tone: "full",
      chartLabels: [null, null, null, null, null, null, null],
      attrs: { md: "4", sm: "6" },
      datasets: [
        {
          label: "Today",
          fill: "start",
          borderWidth: 1.5,
          backgroundColor: "rgba(255,180,0,0.1)",
          borderColor: "rgb(255,180,0)",
          data: [],
        },
      ],
    },
  ];

  const [filterValue, setFilterValue] = useState("");

  let smallStats2 = [
    {
      state: "Online",
      translatedState: t("online"),
      icon: <SignalCellular4BarIcon />,
      label: `${t("sensors")}`,
      value: sensorState.online,
      tone: "online",
      chartLabels: [null, null, null, null, null, null, null],
      attrs: { md: "4", sm: "6" },
      datasets: [
        {
          label: "Today",
          fill: "start",
          borderWidth: 1.5,
          backgroundColor: "rgba(255,180,0,0.1)",
          borderColor: "rgb(255,180,0)",
          data: [],
        },
      ],
    },
    {
      translatedState: t("low_batt"),
      state: "low_batt",
      icon: <i className="fas fa-battery-quarter"></i>,
      label: `${t("sensors")}`,
      value: sensorState.lowBatt,
      tone: "battery",
      chartLabels: [null, null, null, null, null, null, null],
      attrs: { md: "4", sm: "6" },
      datasets: [
        {
          label: "Today",
          fill: "start",
          borderWidth: 1.5,
          backgroundColor: "rgba(255,180,0,0.1)",
          borderColor: "rgb(255,180,0)",
          data: [],
        },
      ],
    },
    {
      translatedState: t("offline"),
      state: "Offline",
      icon: <SignalCellularNodataIcon />,
      label: `${t("sensors")}`,
      value: sensorState.offline,
      tone: "offline",
      chartLabels: [null, null, null, null, null, null, null],
      attrs: { md: "4", sm: "6" },
      datasets: [
        {
          label: "Today",
          fill: "start",
          borderWidth: 1.5,
          backgroundColor: "rgba(255,180,0,0.1)",
          borderColor: "rgb(255,180,0)",
          data: [],
        },
      ],
    },
  ];

  const FilterByStatus = async (status) => {
    let data = {
      status: status,
    };
    await api
      .post("/field/field-status", data)
      .then((response) => {
        let farmsData = response.data.farms;
        setFarms(farmsData);
      })
      .catch((err) => {
        swal({
          title: t("Error"),
          icon: "error",
        });
      });
  };

  const ToSensorPage = () => {
    navigate("/Sensors");
  };

  const ToAddSensorPage = () => {
    navigate("/AddSensor");
  };

  const isSubUser = !!JSON.parse(localStorage.getItem("user") || "{}")?.head;

  useEffect(() => {
    const getDataFields = async () => {
      await api.get("/field/fields").then((res) => {
        const newData = res.data.farms;
        const filteredFarms = newData
          .map((farm) => ({
            ...farm,
            fields: farm.fields?.filter((field) => !field.deleted_at) || [],
          }))
          .filter((farm) => farm.fields.length > 0);

        setFarms(filteredFarms);
        let Fields = [];
        let Zones = [];
        let Crops = [];

        filteredFarms.forEach((item) => {
          let fields = item.fields;
          if (fields) {
            fields.forEach((itemfield) => {
              Fields.push({
                title: itemfield.name,
                status: itemfield.status,
                description: itemfield.description,
                Uid: itemfield.uid,
                farm_id: itemfield.farm_id,
                Latitude: itemfield.Latitude,
                Longitude: itemfield.Longitude,
                Id: itemfield.id,
              });
              let zones = itemfield.zones;
              let crops = itemfield.crops;
              if (crops) {
                crops.forEach((crop) => {
                  Crops.push({
                    type: crop.type,
                    id: crop.id,
                    Uid: crop.uid,
                    fieldId: crop.field_id,
                    zone_id: crop.zone_id,
                    croptype_id: crop.croptype_id,
                    croptype: crop.croptypes,
                    photo: crop.croptypes.crop_photo,
                  });
                });
              }
              if (zones) {
                zones.forEach((i) => {
                  Zones.push({
                    Id: i.id,
                    name: i.name,
                    Uid: i.uid,
                    source: i.source,
                    description: i.description,
                    field_id: i.field_id,
                  });
                });
              }
            });
          }
        });

        setCrops(Crops);
      });
    };
    getDataFields();
  }, []);

  const totalFields = fieldStats.reduce(
    (total, farm) => total + (farm.fields?.length || 0),
    0
  );
  const totalSensors = Array.isArray(sensorStats) ? sensorStats.length : 0;
  const greeting = getGreeting();
  const WeatherIcon = getWeatherIcon(todayWeather?.icon);
  const userName = JSON.parse(localStorage.getItem("user") || "{}")?.name;

  return (
    <Container fluid className="main-content-container farm-overview-page">
      <header className="farm-overview-header" aria-labelledby="overview-heading">
        <div className="farm-overview-title">
          <span className="farm-overview-eyebrow">SMARTFARM</span>
          <p className="farm-overview-greeting">
            <greeting.Icon className="farm-overview-greeting-icon" aria-hidden="true" />
            <span>{userName
              ? t(greeting.key, { name: userName, defaultValue: `Hello, ${userName}` })
              : t(`${greeting.key}_no_name`, { defaultValue: "Hello" })}</span>
          </p>
          <h1 id="overview-heading">{t("overview")}</h1>
          <p className="farm-overview-subtitle">
            {t("overview_dashboard_subtitle", {
              defaultValue:
                "Monitor field health, sensor connectivity and farm activity at a glance.",
            })}
          </p>
        </div>
        <div className="farm-overview-header-actions">
          {!isSubUser && (
            <Dropdown className="farm-overview-add-menu">
              <Dropdown.Toggle id="overview-add-menu" className="farm-overview-add-toggle">
                <Plus aria-hidden="true" />
                <span>{t("add_element")}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu align="end" className="farm-overview-dropdown-menu">
                <Dropdown.Item as={Link} to="/add-farm">
                  <Tractor aria-hidden="true" /> {t("add_farm")}
                </Dropdown.Item>
                <Dropdown.Item onClick={ToAddSensorPage}>
                  <Radio aria-hidden="true" /> {t("sensors")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
        <div className="farm-overview-quick-stats">
          <div className="farm-overview-hero-stat" aria-busy={statsLoading}>
            <span className="farm-overview-hero-stat-icon"><MapPinned aria-hidden="true" /></span>
            <div className="farm-overview-hero-stat-content">
              <strong>{statsLoading ? "\u2014" : totalFields}</strong>
              <span>{t("fields")}</span>
            </div>
          </div>
          <div className="farm-overview-hero-stat" aria-busy={statsLoading}>
            <span className="farm-overview-hero-stat-icon"><Radio aria-hidden="true" /></span>
            <div className="farm-overview-hero-stat-content">
              <strong>{statsLoading ? "\u2014" : totalSensors}</strong>
              <span>{t("sensors")}</span>
            </div>
          </div>
          {todayWeather && (
            <button
              type="button"
              className="farm-overview-hero-stat farm-overview-weather-card"
              onClick={() => navigate("/Weather")}
              aria-label={`${t("weather_forecast")}, ${todayWeather.temp}\u00b0C`}
              title={t("weather_forecast")}
            >
              <span className="farm-overview-hero-stat-icon">
                <WeatherIcon aria-hidden="true" />
              </span>
              <span className="farm-overview-hero-stat-content">
                <strong>{todayWeather.temp}°C</strong>
                <span>{t("weather_today", { defaultValue: "Weather" })}</span>
              </span>
              <ArrowUpRight className="farm-overview-weather-arrow" aria-hidden="true" />
            </button>
          )}
        </div>
      </header>

      {/* Small Stats Blocks */}
      <Row className="farm-overview-summary align-items-stretch">
        <Col lg="4" sm="12" className="farm-overview-summary-group">
          <p className="farm-overview-section-label">
            {t("field_stats")}
          </p>
          <Card className="farm-overview-card farm-overview-stats-card h-100">
            <Card.Body className="p-2 d-flex  justify-content-center align-items-center">
              {statsLoading ? (
                <div className="farm-overview-stats-skeleton" aria-hidden="true">
                  <div className="farm-overview-skeleton-box" />
                  <div className="farm-overview-skeleton-box" />
                  <div className="farm-overview-skeleton-box" />
                </div>
              ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  width: "100%",
                }}
              >
                {smallStats.map((stats, idx) => (
                  <React.Fragment key={idx}>
                    <SmallStats
                      onClick={() => {
                        console.log(filterValue);
                        return setFilterValue(stats.state);
                      }}
                      FilterByStatus={FilterByStatus}
                      id={`small-stats-${idx}`}
                      variation="1"
                      chartData={stats.datasets}
                      chartLabels={stats.chartLabels}
                      label={stats.label}
                      value={stats.value}
                      icon={stats.icon}
                      state={stats.translatedState}
                      tone={stats.tone}
                    />
                    {idx !== 2 && (
                      <hr
                        style={{
                          backgroundColor: "var(--sf-night-surface-alt, #ebebeb)",
                          width: "1px",
                          height: "75%",
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg="4" sm="12" className="farm-overview-summary-group">
          <p className="farm-overview-section-label">
            {t("sensor_stats")}
          </p>
          <Card className="farm-overview-card farm-overview-stats-card h-100">
            <Card.Body className="p-2 d-flex flex-column justify-content-center align-items-center">
              {statsLoading ? (
                <div className="farm-overview-stats-skeleton" aria-hidden="true">
                  <div className="farm-overview-skeleton-box" />
                  <div className="farm-overview-skeleton-box" />
                  <div className="farm-overview-skeleton-box" />
                </div>
              ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  width: "100%",
                }}
              >
                {smallStats2.map((stats, idx) => (
                  <React.Fragment key={idx}>
                    <SmallStats
                      ToSensorPage={ToSensorPage}
                      onClick={() => {
                        console.log(filterValue);
                        return setFilterValue(stats.state);
                      }}
                      id={`small-stats-${idx}`}
                      variation="1"
                      chartData={stats.datasets}
                      chartLabels={stats.chartLabels}
                      label={stats.label}
                      value={stats.value}
                      icon={stats.icon}
                      state={stats.translatedState}
                      tone={stats.tone}
                    />
                    {idx !== 2 && (
                      <hr
                        style={{
                          backgroundColor: "var(--sf-night-surface-alt, #ebebeb)",
                          width: "1px",
                          height: "75%",
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
              )}
              {!statsLoading && sensorState.unavailable > 0 && (
                <p className="farm-overview-sensor-unavailable">
                  <i className="fas fa-circle-info" aria-hidden="true" />
                  {t("sensor_data_unavailable", {
                    count: sensorState.unavailable,
                    defaultValue: `Données indisponibles pour ${sensorState.unavailable} capteur(s)`,
                  })}
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg="4" md="12" sm="12" className="farm-overview-summary-group farm-overview-map-column">
          <p className="farm-overview-section-label">
            {t("map")}
          </p>
          <div className="farm-overview-card farm-overview-map-wrap">
            <div className="farm-overview-map-meta">
              <span>{t("overview_map_live", { defaultValue: "Live overview" })}</span>
              <strong>{layerFarm.length}</strong>
            </div>
            <LeafletMap
              data={layerFarm}
              sensor={sensorStats}
              draw={configMap.draw}
              zoom={mapConfig.zoom}
              center={mapConfig.center}
              fromAction={mapConfig.fromAction}
            />
          </div>
        </Col>
      </Row>

      <section className="farm-overview-card farm-overview-fields-card">
        <div className="farm-overview-card-heading">
          <div>
            <span className="farm-overview-eyebrow">SMARTFARM</span>
            <h2>{t("overview_fields_title", { defaultValue: "Fields overview" })}</h2>
            <p>
              {t("overview_fields_subtitle", {
                defaultValue: "Search, filter and open your fields from one place.",
              })}
            </p>
          </div>
        </div>
        <FilterFields
          smallStats={smallStats}
          sensorsData={sensorsData}
          crops={crops}
          filteredByStatus={farms}
          sensorStats={sensorStats}
          fieldStats={fieldStats}
          value={filterValue}
          setValue={setFilterValue}
        />
      </section>
    </Container>
  );
};

export default Overview;
